var _SearchIcon, _ChevronUp, _ChevronDown;
import clsx from 'clsx';
import { noop } from 'lodash';
import { useMergeRefs } from '@floating-ui/react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Input } from '@ovpn-ui/core';
import { Search as SearchIcon, ChevronDown, ChevronUp, X } from '@ovpn-ui/icons';
import useSearch from '../_internal/hooks/useDropdown';
import maybeDataTestId from '../_internal/utils/maybeDataTestId';
import SearchOptions from '../_internal/components/Dropdown/DropdownOptions';
import { DropdownMenu } from '../_internal/components/Dropdown/Dropdown.styles';
import { Wrapper, IconsContainer } from './Search.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Search({
  title,
  description,
  placeholder = 'Search',
  icon,
  searchDebounceMs,
  options: _options = [],
  disabled = false,
  value,
  onSearch,
  'data-testid': dataTestId,
  inputRef,
  onKeyDown,
  className,
  style,
  error,
  warning,
  success,
  ...props
}, externalRef) {
  const {
    options,
    showOptions,
    searchValue,
    selectedValue,
    isSelected,
    openOptions,
    closeOptions,
    useFloatingProps,
    handleSearch,
    setSelectedValue,
    handleCancel
  } = useSearch({
    query: value,
    multiple: false,
    placement: 'bottom',
    searchDebounceMs,
    options: _options,
    onSearch,
    clearSearch: false
  });
  const internalRef = useRef(null);
  const ref = useMergeRefs([externalRef, internalRef]);
  const {
    floatingProps,
    referenceProps
  } = useFloatingProps(internalRef);
  const isAutocomplete = (_options == null ? void 0 : _options.length) > 0;
  useEffect(() => {
    if (searchValue) {
      openOptions();
    }
    if (searchValue.length === 1 || selectedValue.id) {
      closeOptions();
    }
  }, [selectedValue, searchValue, openOptions, closeOptions]);
  const handleClickItem = option => {
    handleSearch == null ? void 0 : handleSearch(option.id);
    setSelectedValue(option);
    closeOptions();
  };
  const handleOnChange = useCallback(newValue => {
    handleSearch(newValue);
    setSelectedValue({
      title: '',
      id: ''
    });
  }, [handleSearch, setSelectedValue]);
  return /*#__PURE__*/_jsxs(Wrapper, {
    ref: ref,
    className: clsx('ovpn-search', className),
    "data-invalid": warning || error || undefined,
    style: style,
    ...maybeDataTestId(dataTestId),
    children: [/*#__PURE__*/_jsx(Input, {
      inputRef: inputRef,
      type: "search",
      role: "searchbox",
      title: title,
      description: description,
      placeholder: placeholder,
      disabled: disabled,
      error: error,
      warning: warning,
      success: success,
      value: searchValue || selectedValue.title,
      onChange: handleOnChange,
      iconLeft: _SearchIcon || (_SearchIcon = /*#__PURE__*/_jsx(SearchIcon, {
        size: "md"
      })),
      iconRight: /*#__PURE__*/_jsxs(IconsContainer, {
        children: [searchValue && /*#__PURE__*/_jsx(X, {
          size: "md",
          onClick: handleCancel
        }), isAutocomplete && (showOptions ? _ChevronUp || (_ChevronUp = /*#__PURE__*/_jsx(ChevronUp, {
          size: "md"
        })) : _ChevronDown || (_ChevronDown = /*#__PURE__*/_jsx(ChevronDown, {
          size: "md"
        })))]
      }),
      ...referenceProps,
      ...props,
      onKeyDown: onKeyDown
    }), isAutocomplete ? showOptions && /*#__PURE__*/_jsx(DropdownMenu, {
      role: "menu",
      ...floatingProps,
      children: /*#__PURE__*/_jsx(SearchOptions, {
        variant: "input",
        options: options,
        withSearchHighlight: false,
        multiple: false,
        selectAll: false,
        handleSelectAll: noop,
        isSelected: isSelected,
        handleClickItem: handleClickItem,
        checkedIcon: true,
        multipleSelectedValues: options,
        searchValue: searchValue,
        icon: icon
      })
    }) : null]
  });
}
export default /*#__PURE__*/forwardRef(Search);